import matter from "gray-matter";
import Fuse from "fuse.js";
import i18n from "../i18n";

export type Platform = "web" | "android" | "ios" | "windows" | "macos" | "linux";
export type Flavor = "regular" | "ebm";
export type Persona = "owner" | "manager" | "cashier" | "accountant" | "admin";

export type DocMeta = {
    title: string;
    slug: string; // unique path under /docs/
    module: string; // section directory
    contentType: "concept" | "how-to" | "reference" | "troubleshooting";
    platforms: Platform[];
    flavors: Flavor[];
    personas: Persona[];
    summary: string;
    version: string;
    lastUpdated: string; // ISO date string
    toc: boolean;
    order: number;
    tags: string[];
    hero?: string;
    attachments?: string[];
    embeds?: Array<{ type: "youtube"; id: string; title?: string }>;
};

export type DocIndex = {
    slug: string;
    title: string;
    section: string;
    order?: number;
    meta?: Partial<DocMeta> & { summary?: string; tags?: string[] };
};

const DOCS_BASE = "/docs";

/** Fetch JSON helper with error handling */
async function fetchJson<T>(path: string): Promise<T> {
    const res = await fetch(path, { cache: "no-cache" });
    if (!res.ok) throw new Error(`Failed to fetch ${path}: ${res.status}`);
    return res.json() as Promise<T>;
}

/** Load docs manifest, sorted by section then order then title */
export async function loadManifest(): Promise<DocIndex[]> {
    const lang = (i18n?.language || "en").split("-")[0];
    let manifest: DocIndex[];
    try {
        manifest = await fetchJson<DocIndex[]>(`${DOCS_BASE}/${lang}/docs-manifest.json`);
    } catch {
        manifest = await fetchJson<DocIndex[]>(`${DOCS_BASE}/docs-manifest.json`);
    }
    // Logical section order (fallback to alpha if unknown)
    const sectionOrder = [
        "getting-started",
        "home",
        "settings",
        "transactions",
        "inventory",
        "payments",
        "expenses",
        "analytics",
        "users-permissions",
        "flavors",
        "troubleshooting",
        "glossary",
    ];
    const rank = (s: string) => {
        const idx = sectionOrder.indexOf(s);
        return idx === -1 ? Number.MAX_SAFE_INTEGER : idx;
    };
    return [...manifest].sort((a, b) => {
        if (a.section !== b.section) return rank(a.section) - rank(b.section) || a.section.localeCompare(b.section);
        const ao = a.order ?? 0;
        const bo = b.order ?? 0;
        if (ao !== bo) return ao - bo;
        return a.title.localeCompare(b.title);
    });
}

/** Load a markdown doc by slug, parse frontmatter and return meta+content */
export async function loadDoc(slug: string): Promise<{ meta: DocMeta; content: string }> {
    const normalized = slug.replace(/^\/+|\/+$/g, "");
    const lang = (i18n?.language || "en").split("-")[0];
    const candidates = [
        `${DOCS_BASE}/${lang}/${normalized}.md`,
        `${DOCS_BASE}/${lang}/${normalized}/index.md`,
        `${DOCS_BASE}/${normalized}.md`,
        `${DOCS_BASE}/${normalized}/index.md`,
    ];
    let text: string | null = null;
    let lastError: unknown = null;
    for (const url of candidates) {
        try {
            const res = await fetch(url, { cache: "no-cache" });
            if (res.ok) {
                const body = await res.text();
                const looksLikeHtml = /^\s*<!doctype html>|^\s*<html[\s>]/i.test(body);
                if (looksLikeHtml) {
                    continue;
                }
                text = body;
                break;
            }
        } catch (e) {
            lastError = e;
        }
    }
    if (!text) throw new Error(`Doc not found for slug: ${slug} (${String(lastError)})`);

    const { data, content } = matter(text);
    // Basic validation of required frontmatter
    const required: Array<keyof DocMeta> = [
        "title",
        "slug",
        "module",
        "contentType",
        "platforms",
        "flavors",
        "personas",
        "summary",
        "version",
        "lastUpdated",
        "toc",
        "order",
        "tags",
    ];
    for (const key of required) {
        if (!(key in data)) {
            throw new Error(`Missing required frontmatter field '${key}' in ${slug}`);
        }
    }
    const meta = data as DocMeta;
    return { meta, content };
}

/** List docs by section/module */
export async function listDocsBySection(section: string): Promise<DocIndex[]> {
    const manifest = await loadManifest();
    return manifest.filter((d) => d.section === section);
}

/** Client-side search using Fuse.js over manifest */
let fuse: Fuse<DocIndex> | null = null;
async function getFuse(): Promise<Fuse<DocIndex>> {
    if (fuse) return fuse;
    const manifest = await loadManifest();
    fuse = new Fuse(manifest, {
        includeScore: true,
        threshold: 0.35,
        ignoreLocation: true,
        keys: [
            "title",
            "section",
            "slug",
            "meta.summary",
            "meta.tags",
        ],
    });
    return fuse;
}

export async function searchDocs(query: string): Promise<DocIndex[]> {
    const f = await getFuse();
    if (!query.trim()) return (await loadManifest()).slice(0, 50);
    return f.search(query).map((r) => r.item).slice(0, 50);
}


